import { useCallback, useEffect, useRef, useState } from "react";
import { acknowledgeEvents } from "../../../../../backendapi/activityApi";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";
import type { ActivityViewData } from "../types";

/**
 * Subscribes to `activity_event_interactions` for the given workspace+user
 * and returns the set of acknowledged (read) activity IDs.
 *
 * A single listener shared across all sources -- neither ActivityEventSource
 * nor AsyncTaskSource need to track read state themselves.
 */
function useAcknowledgedIds(
    workspaceId: string,
    userId: string,
    database: DatabaseAdapter | null
): Set<string> {
    const [ackIds, setAckIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!workspaceId || !userId || !database) {
            setAckIds(new Set());
            return;
        }

        let unsubscribe: (() => void) | undefined;
        try {
            unsubscribe = database.onQuerySnapshot(
                "activity_event_interactions",
                {
                    where: [
                        ["owner", "==", workspaceId],
                        ["user", "==", userId]
                    ]
                },
                (docs) => {
                    const ids = new Set<string>();
                    for (const doc of docs) {
                        const data = doc.data();
                        if (data?.ackAt && data?.activityEvent) {
                            ids.add(data.activityEvent as string);
                        }
                    }
                    setAckIds(ids);
                },
                // eslint-disable-next-line no-console
                (err) => console.error("activity_event_interactions listener error:", err)
            );
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("useAcknowledgedIds: onQuerySnapshot threw synchronously:", err);
        }

        return () => unsubscribe?.();
    }, [workspaceId, userId, database]);

    return ackIds;
}

/**
 * Manages read/unread state for activities by combining persistent
 * acknowledgements from Firestore with optimistic local state.
 *
 * Returns a pure `applyReadState` callback that stamps `read: true`
 * on activities whose IDs appear in the ack set or local read set,
 * plus `onMarkRead` / `onMarkAllRead` for user interactions.
 *
 * `onMarkAllRead` needs the current source activities to find unread IDs.
 * The caller passes the raw source output via `sourceActivities` so this
 * hook can apply read state internally — no render-phase side effects.
 */
export function useReadState({
    workspaceId,
    userId,
    database,
    sourceActivities
}: {
    workspaceId: string;
    userId: string;
    database: DatabaseAdapter | null;
    sourceActivities: ActivityViewData[];
}): {
    applyReadState: (activities: ActivityViewData[]) => ActivityViewData[];
    onMarkRead: (activity: ActivityViewData) => void;
    onMarkAllRead: () => void;
} {
    const ackIds = useAcknowledgedIds(workspaceId, userId, database);
    const [localReadIds, setLocalReadIds] = useState<Set<string>>(new Set());

    // Refs keep onMarkAllRead stable while always reading fresh values
    const sourceActivitiesRef = useRef(sourceActivities);
    sourceActivitiesRef.current = sourceActivities;
    const ackIdsRef = useRef(ackIds);
    ackIdsRef.current = ackIds;
    const localReadIdsRef = useRef(localReadIds);
    localReadIdsRef.current = localReadIds;

    const applyReadState = useCallback(
        (src: ActivityViewData[]): ActivityViewData[] =>
            src.map((a) => (ackIds.has(a.id) || localReadIds.has(a.id) ? { ...a, read: true } : a)),
        [ackIds, localReadIds]
    );

    const onMarkRead = useCallback(
        (activity: ActivityViewData) => {
            setLocalReadIds((prev) => {
                const next = new Set(prev);
                next.add(activity.id);
                return next;
            });
            acknowledgeEvents(workspaceId, [activity.id]).catch((err) => {
                // eslint-disable-next-line no-console
                console.warn("onMarkRead: ack failed, reverting optimistic update:", err);
                setLocalReadIds((prev) => {
                    const next = new Set(prev);
                    next.delete(activity.id);
                    return next;
                });
            });
        },
        [workspaceId]
    );

    const onMarkAllRead = useCallback(() => {
        const currentAckIds = ackIdsRef.current;
        const currentLocalReadIds = localReadIdsRef.current;
        const unreadIds = sourceActivitiesRef.current
            .filter((a) => !currentAckIds.has(a.id) && !currentLocalReadIds.has(a.id))
            .map((a) => a.id);
        if (unreadIds.length === 0) return;
        setLocalReadIds((prev) => {
            const next = new Set(prev);
            for (const id of unreadIds) next.add(id);
            return next;
        });
        acknowledgeEvents(workspaceId, unreadIds).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn("onMarkAllRead: ack failed, reverting optimistic update:", err);
            setLocalReadIds((prev) => {
                const next = new Set(prev);
                for (const id of unreadIds) next.delete(id);
                return next;
            });
        });
    }, [workspaceId]);

    return { applyReadState, onMarkRead, onMarkAllRead };
}
